import { plural } from '../format';

/**
 * The site area's words (console redesign, docs/console-redesign-plan.md).
 *
 * One file per area so the redesign's parallel work never edits the same
 * dictionary. `en.ts` and `es.ts` spread these in; the Spanish half is typed
 * against the English one, so a key missing here is a build failure exactly as
 * it is in the main catalog.
 *
 * The Website tab (B3): the questionnaire, their page, the address and the
 * photos, and the guided screen of their answers. Plain words throughout — no
 * status tokens, no settings names, no migration numbers; the owner's own
 * words and the server's sentences are quoted as they come.
 */
export const siteEn = {
  'site.fence': ({ name }: { name: string }) =>
    `The website belongs to the second stage, taking bookings online. Nothing here blocks ${name}’s switch-over.`,
  'site.back': '← Website',

  /* ---- the questionnaire ------------------------------------------- */

  'site.q.title': 'Questionnaire',
  'site.q.none':
    'No questionnaire is open. It is one private link the owner fills in on their phone, over as many sittings as they like.',
  'site.q.chip.notOpened': 'Sent, not opened',
  'site.q.chip.filling': 'Being filled in',
  'site.q.chip.answered': 'Answered',
  'site.q.chip.used': 'On their page',
  'site.q.chip.closed': 'Closed',
  'site.q.chip.expired': 'Link expired',
  'site.q.step.made': 'Link made',
  'site.q.step.sent': 'Sent',
  'site.q.step.opened': 'Opened',
  'site.q.step.answered': 'Answered',
  'site.q.step.used': 'Used on the page',
  'site.q.answers': 'Answers',
  'site.q.notOpened': 'Not opened yet',
  'site.q.progress': ({ answered, total }: { answered: number; total: number }) =>
    `${String(answered)} of ${String(total)} answered`,
  'site.q.sentBack': ({ day }: { day: string }) => `sent back ${day}`,
  'site.q.photos': 'Photos',
  'site.q.noPhotos': 'None yet',
  'site.q.photoCount': ({ count }: { count: number }) =>
    `${String(count)} ${plural(count, 'photo', 'photos')}`,
  'site.q.until': 'Link works until',
  'site.q.extend': 'Extend',
  'site.q.extendBy': 'Days from today',
  'site.q.extendGo': ({ days }: { days: number }) =>
    `Make it work ${String(days)} more ${plural(days, 'day', 'days')}`,
  'site.q.extended': ({ days }: { days: number }) =>
    `The link now works for ${String(days)} ${plural(days, 'day', 'days')} from today.`,
  'site.q.form': 'Form',
  'site.q.formIs': ({ template, language }: { template: string; language: string }) =>
    `${template} · in ${language}`,
  'site.q.days': 'Link works for (days)',
  'site.q.review': 'Review their answers',
  'site.q.reviewLater':
    'Their answers can be reviewed here as soon as they start filling it in. Nothing they type is published until you publish their page.',
  'site.q.remintTitle': 'Make a new link?',
  'site.q.reopen': 'Reopen',
  'site.q.reopened': 'The form is open again. The same link works.',
  'site.q.close': 'Close the form',
  'site.q.closeTitle': 'Close the questionnaire?',
  'site.q.closeConsequence':
    'The link stops working and the owner can no longer add anything. Their answers stay here for you to read, and a new link can be made later.',
  'site.q.closed': 'The questionnaire is closed.',

  'site.lang.en': 'English',
  'site.lang.es': 'Spanish',

  /* ---- their answers, one screen ----------------------------------- */

  'site.review.title': 'Their answers',
  'site.review.lead':
    'In the order they were asked: what they wrote, where it goes on their page, and what needs a hand from you. Nothing here is published until you publish their page.',
  'site.review.none': 'No questionnaire has been sent to this business yet.',
  'site.review.countsLabel': 'Where their answers go',
  'site.review.onPage': ({ count }: { count: number }) => `${String(count)} on the page`,
  'site.review.needHand': ({ count }: { count: number }) =>
    `${String(count)} ${plural(count, 'needs', 'need')} a hand`,
  'site.review.elsewhere': ({ count }: { count: number }) => `${String(count)} used elsewhere`,
  'site.review.unanswered': ({ count }: { count: number }) => `${String(count)} not answered`,
  'site.review.handTitle': 'What needs a hand',
  'site.review.handLead':
    'These answers could not go on the page by themselves. Place them in the page editor, or shorten them first.',
  'site.review.sectionCount': ({ answered, total }: { answered: number; total: number }) =>
    `${String(answered)} of ${String(total)} answered`,
  'site.review.notAnswered': 'Not answered',
  'site.review.notOnPage': 'Not on the page — used for the business itself',
  'site.review.notesTitle': 'For you, not for the page',
  'site.review.notesLead':
    'What the owner told you to keep in mind while you edit — words never to use, who walks through the door, how it should feel.',

  'site.reason.tooLong': 'Too long for its place',
  'site.reason.slotFull': 'Its place on the page is full',
  'site.reason.noSection': 'This page has no place for it',
  'site.reason.noPage': 'The page it belongs on does not exist',
  'site.reason.nothingUsable': 'Nothing in it can be published',
  'site.reason.other': 'Could not be placed',

  'site.answer.none': 'None',
  'site.answer.blank': 'Left blank',
  'site.hours.closed': 'closed',

  'site.start.button': 'Start from their answers',
  'site.start.title': 'Start their page from their answers?',
  'site.start.note':
    'The page editor opens with a draft: the template’s layout with their words already in place. Nothing is saved and nothing is published until you press Publish there.',
  'site.start.template': 'Layout to start from',
  'site.start.go': 'Open the draft',

  'site.week.title': 'Their hours and the days they are shut',
  'site.week.lead':
    'These two answers are the book, not the page: they decide when guests, the staff app and partners can book.',
  'site.week.fence': ({ name }: { name: string }) =>
    `Only while a business is being set up. ${name} is past that, so their hours and closed days are theirs to change in their own app, and this screen cannot write them.`,
  'site.week.hours': 'Their week',
  'site.week.noHours': 'They have not filled in their week yet.',
  'site.week.applyHours': 'Apply these hours',
  'site.week.hoursTitle': ({ name }: { name: string }) => `Set ${name}’s opening hours?`,
  'site.week.hoursConsequence': ({ week }: { week: string }) =>
    `From now on, times are offered only inside this week — online, in the staff app and to partners: ${week}. It replaces the hours they have now.`,
  'site.week.hoursApplied': 'Their opening hours are set.',
  'site.week.closures': 'Days they are shut',
  'site.week.noClosures': 'They listed no closed days.',
  'site.week.applyClosures': 'Apply these closures',
  'site.week.closuresTitle': ({ name }: { name: string }) => `Close ${name} on these days?`,
  'site.week.closuresConsequence': ({ days }: { days: string }) =>
    `Nobody can book on these days — online, in the staff app or through a partner: ${days}.`,
  'site.week.closuresApplied': ({ count }: { count: number }) =>
    `${String(count)} ${plural(count, 'closure', 'closures')} written.`,
  'site.week.written': 'Already written',
  'site.week.removeTitle': 'Open these days again?',
  'site.week.removeConsequence': ({ days }: { days: string }) =>
    `${days} can be booked again, at their usual hours.`,
  'site.week.closureRemoved': 'Those days are open again.',

  'site.menu.title': 'Their menu',
  'site.menu.count': ({ count }: { count: number }) =>
    `${String(count)} ${plural(count, 'item', 'items')}`,
  'site.menu.lead':
    'The list they typed, drafted as a catalog. Seeding it here shows what it would add in What they sell, as a trial — nothing is saved until you save it there.',
  'site.menu.empty':
    'They did not write a list. Either their catalog was already in when they filled the form in, or their menu is in their photos.',
  'site.menu.needsPerson': 'Still needs a person',
  'site.menu.needsPersonLead':
    'A length or a price is read only where their words mean one thing. A range, a “from” or a blank is not a number, and nothing is guessed. Settle these with them.',
  'site.menu.said': ({ said }: { said: string }) => `they said: ${said}`,
  'site.menu.saidNothing': 'left blank',
  'site.menu.notReady':
    'This list cannot be seeded yet: an item needs a length and nobody has given one. Fill those in first.',
  'site.menu.seedNote': 'Opens it in What they sell as a trial run.',
  'site.menu.seedBlocked':
    'Settle the rows above with them first — a guessed length would end up on their page.',
  'site.menu.copyFile': 'Copy the file',
  'site.menu.copyCommand': 'Copy the command',
  'site.menu.showFile': 'Show the file',
  'site.menu.terminal': 'At a terminal:',

  /* ---- their page -------------------------------------------------- */

  'site.page.title': 'Their page',
  'site.page.notPublished': 'Not published',
  'site.page.published': ({ version }: { version: number }) =>
    `Published · version ${String(version)}`,
  'site.page.placeholder': 'Their page appears here once it has a draft',
  'site.page.never':
    'Nothing is published yet. Guests on their address see the plain starting page until you publish one.',
  'site.page.when': 'Published',
  'site.page.whenBy': ({ day, who }: { day: string; who: string }) => `${day} by ${who}`,
  'site.page.someone': 'someone',
  'site.page.note': 'Note',
  'site.page.open': 'Open the page editor',
  'site.page.choose': 'Choose a template',
  'site.page.chooseTitle': 'Choose a template',
  'site.page.chooseLead':
    'The page editor opens with the template’s layout and words as a draft. Nothing changes on their page until you publish it.',
  'site.page.use': 'Use this one',
  'site.page.useLabel': ({ label }: { label: string }) => `Use this one: ${label}`,
  'site.page.restore': 'Restore a version',

  'site.restore.pick': 'Restore an earlier version',
  'site.restore.version': ({ version }: { version: number }) => `Version ${String(version)}`,
  'site.restore.choose': 'Restore',
  'site.restore.title': ({ version }: { version: number }) =>
    `Put version ${String(version)} back on their page?`,
  'site.restore.consequence': ({ version }: { version: number }) =>
    `Version ${String(version)} is published again, as a new version, and every guest sees it on their next visit. Today’s page stays in the list, so this can be undone the same way.`,
  'site.restore.label': ({ version }: { version: number }) => `Restore version ${String(version)}`,
  'site.restore.done': ({ from, version }: { from: number; version: number }) =>
    `Version ${String(from)} is back on their page, as version ${String(version)}.`,

  /* ---- the address ------------------------------------------------- */

  'site.address.title': 'Address',
  'site.address.defaultName': 'Their free address:',
  'site.address.defaultNote':
    'Works from the first day, on our booking domain, with nothing to set up. Their own domain can be added beside it.',
  'site.address.served': 'Working',
  'site.address.notServed': 'Not working yet',
  'site.address.ours': 'This one is on our domain.',
  'site.address.stepsLabel': ({ hostname }: { hostname: string }) => `${hostname}: progress`,
  'site.address.step.added': 'Added',
  'site.address.step.pointing': 'Pointing here',
  'site.address.step.secure': 'Secure',
  'site.address.step.live': 'Live',
  'site.address.meaning.waiting':
    'Waiting for their domain to point here. Nothing is shown on it yet.',
  'site.address.meaning.securing':
    'Their domain points here and its security certificate is being made. Still not shown to guests.',
  'site.address.meaning.live': 'Their site is shown on this address.',
  'site.address.verified': ({ day }: { day: string }) => `Checked ${day}.`,
  'site.address.to.waiting': 'Mark as waiting for their domain',
  'site.address.to.pointing': 'Mark as pointing here',
  'site.address.to.live': 'Mark as live',
  'site.address.moveTitle': ({ hostname, step }: { hostname: string; step: string }) =>
    `${hostname}: mark as “${step}”?`,
  'site.address.consequence.waiting':
    'Their site stops being shown on this address. Anyone signed in on it is signed out, and saved links to it stop working. Nothing else about the business changes.',
  'site.address.consequence.securing':
    'Their site stops being shown on this address while its certificate is made. Anyone signed in on it is signed out.',
  'site.address.consequence.live':
    'Their site starts being shown on this address. Do this only once the certificate is actually working: if it is not, browsers refuse to open the address, which looks exactly like the site being down.',
  'site.address.notTheWorld':
    'This records a step; it does not do it. Their domain’s settings are at their registrar, and the certificate is made by our hosting.',
  'site.address.moved': ({ hostname, step }: { hostname: string; step: string }) =>
    `${hostname} is marked “${step}”.`,
  'site.address.add': 'Add their own domain',
  'site.address.hostname': 'Their domain',
  'site.address.hostnameHint':
    'The address they want guests to use, like book.theirbusiness.com. They point it at us with one setting at their registrar; adding it here does not do that for them.',
  'site.address.save': 'Add the domain',
  'site.address.added': ({ hostname }: { hostname: string }) =>
    `${hostname} is added. It works once their domain points here and its certificate is made.`,
  'site.address.updated': ({ hostname }: { hostname: string }) => `${hostname} is updated.`,
  'site.address.cliOnly':
    'Removing an address, or moving one from another business, is done from the command line, where the address is typed again: both take a working address off the air and sign out everyone on it.',

  /* ---- photos ------------------------------------------------------ */

  'site.photos.title': 'Photos',
  'site.photos.noForm':
    'Photos they upload through the questionnaire land here to pick from. Send them the questionnaire first.',
  'site.photos.empty': 'They have not sent any photos yet.',
  'site.photos.off':
    'Photos cannot be shown here: this setup has no private storage for them yet. Whoever runs the deployment can turn it on.',
  'site.photos.noPublic':
    'You can look and set photos aside, but none can be made public: this setup has no public storage for the site yet.',
  'site.photos.stillNeeded': ({ piles }: { piles: string }) =>
    `Still needed for their page: ${piles}.`,
  'site.photos.pileCount': ({ count }: { count: number }) =>
    `${String(count)} ${plural(count, 'photo', 'photos')}`,
  'site.photos.forPage': 'asked for by their page',
  'site.photos.convert': 'Needs converting first',
  'site.photos.convertWhy':
    'An iPhone photo format browsers do not show. Ask them to send it as a JPEG, or convert it, then upload it again.',
  'site.photos.setAside': 'Set aside',
  'site.photos.public': 'Public',
  'site.photos.promote': 'Make public',
  'site.photos.reject': 'Set aside',
  'site.photos.copyUrl': 'Copy its address',
};

export const siteEs: typeof siteEn = {
  'site.fence': ({ name }: { name: string }) =>
    `La web pertenece a la segunda etapa, recibir reservas en línea. Nada de aquí frena el cambio de ${name}.`,
  'site.back': '← Sitio web',

  'site.q.title': 'Cuestionario',
  'site.q.none':
    'No hay ningún cuestionario abierto. Es un enlace privado que el dueño rellena en su teléfono, en tantas sesiones como quiera.',
  'site.q.chip.notOpened': 'Enviado, sin abrir',
  'site.q.chip.filling': 'Rellenándose',
  'site.q.chip.answered': 'Respondido',
  'site.q.chip.used': 'En su página',
  'site.q.chip.closed': 'Cerrado',
  'site.q.chip.expired': 'Enlace caducado',
  'site.q.step.made': 'Enlace creado',
  'site.q.step.sent': 'Enviado',
  'site.q.step.opened': 'Abierto',
  'site.q.step.answered': 'Respondido',
  'site.q.step.used': 'Usado en la página',
  'site.q.answers': 'Respuestas',
  'site.q.notOpened': 'Aún sin abrir',
  'site.q.progress': ({ answered, total }: { answered: number; total: number }) =>
    `${String(answered)} de ${String(total)} respondidas`,
  'site.q.sentBack': ({ day }: { day: string }) => `devuelto el ${day}`,
  'site.q.photos': 'Fotos',
  'site.q.noPhotos': 'Ninguna todavía',
  'site.q.photoCount': ({ count }: { count: number }) =>
    `${String(count)} ${plural(count, 'foto', 'fotos')}`,
  'site.q.until': 'El enlace funciona hasta',
  'site.q.extend': 'Ampliar',
  'site.q.extendBy': 'Días desde hoy',
  'site.q.extendGo': ({ days }: { days: number }) =>
    `Que funcione ${String(days)} ${plural(days, 'día', 'días')} más`,
  'site.q.extended': ({ days }: { days: number }) =>
    `El enlace funciona ahora ${String(days)} ${plural(days, 'día', 'días')} desde hoy.`,
  'site.q.form': 'Formulario',
  'site.q.formIs': ({ template, language }: { template: string; language: string }) =>
    `${template} · en ${language}`,
  'site.q.days': 'El enlace funciona (días)',
  'site.q.review': 'Revisar sus respuestas',
  'site.q.reviewLater':
    'Sus respuestas se pueden revisar aquí en cuanto empiece a rellenarlo. Nada de lo que escriba se publica hasta que publiques su página.',
  'site.q.remintTitle': '¿Crear un enlace nuevo?',
  'site.q.reopen': 'Reabrir',
  'site.q.reopened': 'El formulario vuelve a estar abierto. Sirve el mismo enlace.',
  'site.q.close': 'Cerrar el formulario',
  'site.q.closeTitle': '¿Cerrar el cuestionario?',
  'site.q.closeConsequence':
    'El enlace deja de funcionar y el dueño ya no puede añadir nada. Sus respuestas se quedan aquí para que las leas, y más adelante se puede crear un enlace nuevo.',
  'site.q.closed': 'El cuestionario está cerrado.',

  'site.lang.en': 'inglés',
  'site.lang.es': 'español',

  'site.review.title': 'Sus respuestas',
  'site.review.lead':
    'En el orden en que se le preguntaron: lo que escribió, dónde va en su página y lo que necesita una mano tuya. Nada de esto se publica hasta que publiques su página.',
  'site.review.none': 'Todavía no se ha enviado ningún cuestionario a este negocio.',
  'site.review.countsLabel': 'Adónde van sus respuestas',
  'site.review.onPage': ({ count }: { count: number }) => `${String(count)} en la página`,
  'site.review.needHand': ({ count }: { count: number }) =>
    `${String(count)} ${plural(count, 'necesita', 'necesitan')} una mano`,
  'site.review.elsewhere': ({ count }: { count: number }) =>
    `${String(count)} ${plural(count, 'usada', 'usadas')} en otro sitio`,
  'site.review.unanswered': ({ count }: { count: number }) => `${String(count)} sin responder`,
  'site.review.handTitle': 'Lo que necesita una mano',
  'site.review.handLead':
    'Estas respuestas no pudieron ir solas a la página. Colócalas en el editor de la página, o acórtalas antes.',
  'site.review.sectionCount': ({ answered, total }: { answered: number; total: number }) =>
    `${String(answered)} de ${String(total)} respondidas`,
  'site.review.notAnswered': 'Sin responder',
  'site.review.notOnPage': 'No va en la página: se usa para el propio negocio',
  'site.review.notesTitle': 'Para ti, no para la página',
  'site.review.notesLead':
    'Lo que el dueño te pidió tener presente al editar: palabras que nunca usar, quién entra por la puerta, qué sensación debe dar.',

  'site.reason.tooLong': 'Demasiado larga para su sitio',
  'site.reason.slotFull': 'Su sitio en la página está lleno',
  'site.reason.noSection': 'Esta página no tiene sitio para ella',
  'site.reason.noPage': 'La página a la que pertenece no existe',
  'site.reason.nothingUsable': 'Nada de ella se puede publicar',
  'site.reason.other': 'No se pudo colocar',

  'site.answer.none': 'Ninguno',
  'site.answer.blank': 'En blanco',
  'site.hours.closed': 'cerrado',

  'site.start.button': 'Empezar con sus respuestas',
  'site.start.title': '¿Empezar su página con sus respuestas?',
  'site.start.note':
    'El editor de la página se abre con un borrador: el diseño de la plantilla con sus palabras ya colocadas. No se guarda ni se publica nada hasta que pulses Publicar allí.',
  'site.start.template': 'Diseño de partida',
  'site.start.go': 'Abrir el borrador',

  'site.week.title': 'Su horario y los días que cierra',
  'site.week.lead':
    'Estas dos respuestas son la agenda, no la página: deciden cuándo pueden reservar los clientes, la app del personal y los socios.',
  'site.week.fence': ({ name }: { name: string }) =>
    `Solo mientras un negocio se está preparando. ${name} ya pasó esa etapa, así que su horario y sus días de cierre los cambia en su propia app, y esta pantalla no puede escribirlos.`,
  'site.week.hours': 'Su semana',
  'site.week.noHours': 'Todavía no ha rellenado su semana.',
  'site.week.applyHours': 'Aplicar este horario',
  'site.week.hoursTitle': ({ name }: { name: string }) => `¿Fijar el horario de ${name}?`,
  'site.week.hoursConsequence': ({ week }: { week: string }) =>
    `A partir de ahora solo se ofrecen horas dentro de esta semana, en línea, en la app del personal y a los socios: ${week}. Sustituye el horario que tiene ahora.`,
  'site.week.hoursApplied': 'Su horario está fijado.',
  'site.week.closures': 'Días que cierra',
  'site.week.noClosures': 'No indicó días de cierre.',
  'site.week.applyClosures': 'Aplicar estos cierres',
  'site.week.closuresTitle': ({ name }: { name: string }) => `¿Cerrar ${name} estos días?`,
  'site.week.closuresConsequence': ({ days }: { days: string }) =>
    `Nadie puede reservar estos días, ni en línea, ni en la app del personal, ni a través de un socio: ${days}.`,
  'site.week.closuresApplied': ({ count }: { count: number }) =>
    `${String(count)} ${plural(count, 'cierre escrito', 'cierres escritos')}.`,
  'site.week.written': 'Ya escritos',
  'site.week.removeTitle': '¿Volver a abrir estos días?',
  'site.week.removeConsequence': ({ days }: { days: string }) =>
    `${days} se puede reservar de nuevo, en su horario habitual.`,
  'site.week.closureRemoved': 'Esos días vuelven a estar abiertos.',

  'site.menu.title': 'Su carta',
  'site.menu.count': ({ count }: { count: number }) =>
    `${String(count)} ${plural(count, 'artículo', 'artículos')}`,
  'site.menu.lead':
    'La lista que escribió, redactada como catálogo. Sembrarla aquí muestra lo que añadiría en Lo que venden, como prueba: no se guarda nada hasta que lo guardes allí.',
  'site.menu.empty':
    'No escribió ninguna lista. O su catálogo ya estaba cargado cuando rellenó el formulario, o su carta está en sus fotos.',
  'site.menu.needsPerson': 'Aún necesita a una persona',
  'site.menu.needsPersonLead':
    'Una duración o un precio solo se leen donde sus palabras significan una sola cosa. Un rango, un «desde» o un blanco no son un número, y no se adivina nada. Resuélvelos con el dueño.',
  'site.menu.said': ({ said }: { said: string }) => `dijo: ${said}`,
  'site.menu.saidNothing': 'en blanco',
  'site.menu.notReady':
    'Esta lista todavía no se puede sembrar: un artículo necesita una duración y nadie la ha dado. Complétalas antes.',
  'site.menu.seedNote': 'La abre en Lo que venden como prueba.',
  'site.menu.seedBlocked':
    'Resuelve antes las filas de arriba con el dueño: una duración adivinada acabaría en su página.',
  'site.menu.copyFile': 'Copiar el archivo',
  'site.menu.copyCommand': 'Copiar el comando',
  'site.menu.showFile': 'Ver el archivo',
  'site.menu.terminal': 'En una terminal:',

  'site.page.title': 'Su página',
  'site.page.notPublished': 'Sin publicar',
  'site.page.published': ({ version }: { version: number }) =>
    `Publicada · versión ${String(version)}`,
  'site.page.placeholder': 'Su página aparece aquí en cuanto tenga un borrador',
  'site.page.never':
    'Todavía no hay nada publicado. Quien visite su dirección ve la página de partida sencilla hasta que publiques una.',
  'site.page.when': 'Publicada',
  'site.page.whenBy': ({ day, who }: { day: string; who: string }) => `${day} por ${who}`,
  'site.page.someone': 'alguien',
  'site.page.note': 'Nota',
  'site.page.open': 'Abrir el editor de la página',
  'site.page.choose': 'Elegir una plantilla',
  'site.page.chooseTitle': 'Elige una plantilla',
  'site.page.chooseLead':
    'El editor de la página se abre con el diseño y los textos de la plantilla como borrador. Nada cambia en su página hasta que la publiques.',
  'site.page.use': 'Usar esta',
  'site.page.useLabel': ({ label }: { label: string }) => `Usar esta: ${label}`,
  'site.page.restore': 'Restaurar una versión',

  'site.restore.pick': 'Restaurar una versión anterior',
  'site.restore.version': ({ version }: { version: number }) => `Versión ${String(version)}`,
  'site.restore.choose': 'Restaurar',
  'site.restore.title': ({ version }: { version: number }) =>
    `¿Volver a poner la versión ${String(version)} en su página?`,
  'site.restore.consequence': ({ version }: { version: number }) =>
    `La versión ${String(version)} se publica de nuevo, como una versión nueva, y cada cliente la ve en su próxima visita. La página de hoy se queda en la lista, así que se puede deshacer igual.`,
  'site.restore.label': ({ version }: { version: number }) =>
    `Restaurar la versión ${String(version)}`,
  'site.restore.done': ({ from, version }: { from: number; version: number }) =>
    `La versión ${String(from)} vuelve a estar en su página, como versión ${String(version)}.`,

  'site.address.title': 'Dirección',
  'site.address.defaultName': 'Su dirección gratuita:',
  'site.address.defaultNote':
    'Funciona desde el primer día, en nuestro dominio de reservas, sin nada que configurar. Su propio dominio se puede añadir al lado.',
  'site.address.served': 'Funcionando',
  'site.address.notServed': 'Aún no funciona',
  'site.address.ours': 'Esta está en nuestro dominio.',
  'site.address.stepsLabel': ({ hostname }: { hostname: string }) => `${hostname}: progreso`,
  'site.address.step.added': 'Añadida',
  'site.address.step.pointing': 'Apunta aquí',
  'site.address.step.secure': 'Segura',
  'site.address.step.live': 'En marcha',
  'site.address.meaning.waiting':
    'Esperando a que su dominio apunte aquí. Todavía no se muestra nada en ella.',
  'site.address.meaning.securing':
    'Su dominio apunta aquí y se está creando su certificado de seguridad. Aún no se muestra a los clientes.',
  'site.address.meaning.live': 'Su web se muestra en esta dirección.',
  'site.address.verified': ({ day }: { day: string }) => `Comprobada el ${day}.`,
  'site.address.to.waiting': 'Marcar como esperando su dominio',
  'site.address.to.pointing': 'Marcar como que apunta aquí',
  'site.address.to.live': 'Marcar como en marcha',
  'site.address.moveTitle': ({ hostname, step }: { hostname: string; step: string }) =>
    `${hostname}: ¿marcar como «${step}»?`,
  'site.address.consequence.waiting':
    'Su web deja de mostrarse en esta dirección. Quien tenga la sesión abierta en ella la pierde, y los enlaces guardados dejan de funcionar. Nada más del negocio cambia.',
  'site.address.consequence.securing':
    'Su web deja de mostrarse en esta dirección mientras se crea su certificado. Quien tenga la sesión abierta en ella la pierde.',
  'site.address.consequence.live':
    'Su web empieza a mostrarse en esta dirección. Hazlo solo cuando el certificado funcione de verdad: si no, los navegadores se niegan a abrirla, y eso parece exactamente que la web está caída.',
  'site.address.notTheWorld':
    'Esto anota un paso; no lo da. Los ajustes de su dominio están en su registrador, y el certificado lo crea nuestro alojamiento.',
  'site.address.moved': ({ hostname, step }: { hostname: string; step: string }) =>
    `${hostname} está marcada como «${step}».`,
  'site.address.add': 'Añadir su propio dominio',
  'site.address.hostname': 'Su dominio',
  'site.address.hostnameHint':
    'La dirección que quiere que usen sus clientes, como reservas.sunegocio.com. La apunta hacia nosotros con un ajuste en su registrador; añadirla aquí no lo hace por él.',
  'site.address.save': 'Añadir el dominio',
  'site.address.added': ({ hostname }: { hostname: string }) =>
    `${hostname} está añadida. Funcionará cuando su dominio apunte aquí y se cree su certificado.`,
  'site.address.updated': ({ hostname }: { hostname: string }) => `${hostname} está actualizada.`,
  'site.address.cliOnly':
    'Quitar una dirección, o pasarla desde otro negocio, se hace desde la línea de comandos, volviendo a escribir la dirección: las dos cosas dejan sin servicio una dirección que funciona y cierran la sesión de todos los que están en ella.',

  'site.photos.title': 'Fotos',
  'site.photos.noForm':
    'Las fotos que suba con el cuestionario llegan aquí para elegir. Envíale antes el cuestionario.',
  'site.photos.empty': 'Todavía no ha enviado ninguna foto.',
  'site.photos.off':
    'Las fotos no se pueden mostrar aquí: esta instalación aún no tiene almacenamiento privado para ellas. Quien gestiona el despliegue puede activarlo.',
  'site.photos.noPublic':
    'Puedes mirarlas y apartarlas, pero ninguna se puede hacer pública: esta instalación aún no tiene almacenamiento público para la web.',
  'site.photos.stillNeeded': ({ piles }: { piles: string }) =>
    `Aún hacen falta para su página: ${piles}.`,
  'site.photos.pileCount': ({ count }: { count: number }) =>
    `${String(count)} ${plural(count, 'foto', 'fotos')}`,
  'site.photos.forPage': 'las pide su página',
  'site.photos.convert': 'Hay que convertirla antes',
  'site.photos.convertWhy':
    'Un formato de foto de iPhone que los navegadores no muestran. Pídele que la envíe como JPEG, o conviértela, y súbela de nuevo.',
  'site.photos.setAside': 'Apartada',
  'site.photos.public': 'Pública',
  'site.photos.promote': 'Hacer pública',
  'site.photos.reject': 'Apartar',
  'site.photos.copyUrl': 'Copiar su dirección',
};
